import React, { useState } from "react";
import { Link as RouterLink } from "react-router";

import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";

import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import DialogContent from "@mui/material/DialogContent";

import Button from "components/Button";
import StyledFormMaskedInput from "components/FormInput/FormMaskedInput";

import { useAppDispatch } from "store/hooks";
import { preOrderIban } from "store/features/preOrderSlice";
import { FormValues, validationSchema } from "./validation";

import { routes } from "routes/routes";

import CabinetImg from "assets/images/cabinet-modal.png";
import { ReactComponent as CloseIcon } from "assets/images/icon_close_modal.svg";

import analytics from "services/analytics";
import { FbPixelAnalyticsEvents } from "shared/constants/fb-pixel-analytics";

import { styles } from "./styles";

interface CallFormModalProps {
  source: string;
  isOpen: boolean;
  closeModal: () => void;

  title?: string;
  description?: string;
}

const WorkInProgressModal: React.FC<CallFormModalProps> = (props) => {
  const { isOpen, source, title, description, closeModal } = props;
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const methods = useForm<FormValues>({
    mode: "onSubmit",
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = () => {
    methods.handleSubmit(async (values: FormValues) => {
      setIsLoading(true);
      dispatch(
        preOrderIban({
          phoneNumber: values.phoneNumber,
          source,
          onSuccess: () => {
            setIsLoading(false);
            closeModal();
            methods.reset();
            analytics().sendConversionEvent(FbPixelAnalyticsEvents.Contact, {
              content_name: source,
            });
          },
          onError: () => {
            setIsLoading(false);
          },
        })
      );
    })();
  };

  const onClose = () => {
    methods.reset();
    setIsLoading(false);
    closeModal();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} PaperProps={{ sx: styles.modal }}>
      <IconButton
        aria-label="close-modal"
        onClick={onClose}
        sx={styles.closeButton}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={styles.content}>
        <Box component="img" alt="cabinet" src={CabinetImg} sx={{ mb: 2 }} />
        <Box sx={{ ml: 2.5, mr: 2.5 }}>
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            {title || "Функціонал у розробці"}
          </Typography>
          <Typography color="grey700" sx={{ mb: 2 }}>
            {description || `Залиште запит, і ми звʼяжемось з вами!`}
          </Typography>
        </Box>
        <FormProvider {...methods}>
          <StyledFormMaskedInput
            mask="+38 (099) 999-9999"
            title="Номер телефону"
            name="phoneNumber"
            fullWidth
            slotProps={{ input: { inputProps: { inputMode: "numeric" } } }}
          />
        </FormProvider>
        <Typography
          variant="body2"
          color="grey700"
          sx={{ textAlign: "left", mb: 2, mt: -1 }}
        >
          Продовжуючи, я погоджуюсь з умовами{" "}
          <Link
            component={RouterLink}
            variant="body2"
            to={routes.termsOfService}
          >
            Політики конфіденційності
          </Link>
        </Typography>
        <Box display="flex" justifyContent="flex-end">
          <Button
            sx={[{ ml: { xs: 0, sm: 2 }, minWidth: { sm: 173 } }]}
            variant="contained"
            color="primary"
            onClick={onSubmit}
            disabled={isLoading}
            loading={isLoading}
          >
            Зателефонуйте мені
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WorkInProgressModal;
